import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Breadcrumbs, Crumb } from '@carsys/ui'
import {
  getLineDescription,
  getLineGenericArticleDescription,
  getProductTitle,
  getRepairTimeChapterName,
  useGetCurrentSelectedOrderAccount
} from 'app/workorder/state/workorder/selectors'
import {
  WarrantyClaimsUrl,
  WomPlusUrl,
  WorkOrderInvoicesUrl,
  WorkOrderQuotationDetailUrl,
  WorkOrderQuotationsUrl
} from 'app/workorder/RouteConstants'
import { UUIDPathRegex } from '@cbgms/base/utils/uuid'
import { useProductGroupCrumbs } from './ProductGroupCrumbs'

export const WorkOrdersBreadCrumbs: React.FC<React.ComponentProps<typeof Breadcrumbs>> = ({ ...props }) => {
  const { t } = useTranslation()
  const lines = useSelector(state => state.workorder.lines)
  const product = useSelector(state => state.products.selectedProduct)
  const productGroups = useSelector(state => state.products.categories)
  const repairTimeChapters = useSelector(state => state.workorder.repairTimes.chapters)
  const workorderID = useSelector(state => state.workorder.details.ID)
  const selectedOrderAccount = useGetCurrentSelectedOrderAccount()
  const productGroupCrumbs = useProductGroupCrumbs({ productGroups, path: '/workorders/:uuid/product-groups/:productGroupUUID' })

  return (
    <Breadcrumbs {...props}>
      <Crumb data-testid='crumb-workorders' key='crumb-workorders' path='/workorders' text={t('workorder.workorders')} />
      <Crumb
        data-testid='crumb-workorders-quotations-list'
        key='crumb-workorders-quotations-list'
        path={WorkOrderQuotationsUrl}
        text={t('VehicleSales.QuotationsListTitle')}
      />
      <Crumb
        data-testid='crumb-workorders-quotation-detail'
        key='crumb-workorders-quotation-detail'
        path={WorkOrderQuotationDetailUrl}
        text={t('VehicleSales.QuotationTitle')}
      />
      <Crumb
        data-testid='crumb-workorders-invoices-list'
        key='crumb-workorders-invoices-list'
        path={WorkOrderInvoicesUrl}
        text={t('workorder.invoices')}
      />
      <Crumb data-testid='crumb-claims-list' key='crumb-claims-list' path={WarrantyClaimsUrl} text={t('claims.title')} />
      <Crumb
        data-testid='crumb-workorders-view'
        key='crumb-workorders-view'
        path={`/workorders/:workorderUUID(${UUIDPathRegex})`}
        text={`${t('workorder.workorder')} (${workorderID})`}
      />
      <Crumb
        data-testid='crumb-workorders-view-invoices-view'
        key='crumb-workorders-view-invoices-view'
        path='/workorders/:uuid/invoices/:invoiceUUID'
        text={t('workorder.invoice')}
      />
      <Crumb
        data-testid='crumb-workorders-view-quick-create'
        key='crumb-workorders-view-quick-create'
        path='/workorders/:uuid/quick-create'
        text={`${t('workorder.new_workorder')}`}
      />
      <Crumb
        data-testid='crumb-workorders-view-product-groups'
        key='crumb-workorders-view-product-groups'
        path='/workorders/:uuid/product-groups'
        text={t('product.product_groups')}
      />
      <Crumb
        data-testid='crumb-workorders-view-wholesaler-iframe'
        key='crumb-workorders-view-wholesaler-iframe'
        path='/workorders/:uuid/wholesaler-iframe'
        text={t('workorder.wholesaler-iframe')}
      />
      <Crumb
        data-testid='crumb-workorders-view-stock-part'
        key='crumb-workorders-view-stock-part'
        path='/workorders/:uuid/stock-part'
        text={t('workorder.add_stock_part')}
      />
      <Crumb
        data-testid='crumb-workorders-view-activities'
        key='crumb-workorders-view-activities'
        path='/workorders/:uuid/activities'
        text={t('workorder.activities')}
      />
      <Crumb
        data-testid='crumb-workorders-view-packages'
        key='crumb-workorders-view-packages'
        path='/workorders/:uuid/packages'
        text={t('workorder.packages.title')}
      />
      <Crumb
        data-testid='crumb-workorders-view-plan-appointment'
        key='crumb-workorders-view-plan-appointment'
        path='/workorders/:uuid/plan-appointment'
        text={t('workorder.create_appointment')}
      />
      <Crumb
        data-testid='crumb-workorders-view-invoice'
        key='crumb-workorders-view-invoice'
        path='/workorders/:uuid/invoice'
        text={t('workorder.invoice_preview')}
      />
      <Crumb
        data-testid='crumb-workorders-view-order'
        key='crumb-workorders-view-order'
        path='/workorders/:uuid/order'
        text={t('workorder.order_parts')}
      />
      <Crumb
        data-testid='crumb-workorders-view-orders'
        key='crumb-workorders-view-orders'
        path='/workorders/:uuid/orders'
        text={t('order.orders')}
      />
      <Crumb
        data-testid='crumb-workorders-view-orders-order'
        key='crumb-workorders-view-orders-order'
        path='/workorders/:uuid/orders/:orderUUID'
        text={t('order.order')}
      />
      <Crumb
        data-testid='crumb-workorders-view-apk'
        key='crumb-workorders-view-apk'
        path='/workorders/:uuid/apk'
        text={t('workorder.apk')}
      />
      <Crumb
        data-testid='crumb-workorders-view-apk-view-transaction-print'
        key='crumb-workorders-view-apk-view-transaction-print'
        path='/workorders/:uuid/apk/:transactionUUID/print/consult-vehicle'
        text={t('APK.ConsultVehicle')}
      />
      <Crumb
        data-testid='crumb-workorders-view-apk-view-inspection-print'
        key='crumb-workorders-view-apk-view-inspection-print'
        path='/workorders/:uuid/apk/:apkUUID/print/inspection-report'
        text={t('APK.InspectionResult')}
      />
      <Crumb
        data-testid='crumb-workorders-view-order-step-2'
        key='crumb-workorders-view-order-step-2'
        path='/workorders/:uuid/order/step2'
        text={selectedOrderAccount.Wholesaler.Name}
      />
      <Crumb data-testid='crumb-workorders-view-rob' key='crumb-workorders-view-rob' path='/workorders/:uuid/rob' text={t('rob.rob')} />
      <Crumb
        data-testid='crumb-workorders-view-print'
        key='crumb-workorders-view-print'
        path='/workorders/:uuid/print'
        text={t('global.print')}
      />
      <Crumb
        data-testid='crumb-workorders-view-labour-rates'
        key='crumb-workorders-view-labour-rates'
        path='/workorders/:uuid/labour-rates'
        text={t('workorder.add_labour_rate')}
      />
      <Crumb
        data-testid='crumb-workorders-view-remarks'
        key='crumb-workorders-view-remarks'
        path='/workorders/:uuid/remarks'
        text={t('workorder.remarks')}
      />
      <Crumb
        data-testid='crumb-workorders-view-link-account'
        key='crumb-workorders-view-link-account'
        path='/workorders/:uuid/link-account'
        text={t('workorder.link_account')}
      />
      <Crumb
        data-testid='crumb-workorders-view-link-invoice-account'
        key='crumb-workorders-view-link-invoice-account'
        path='/workorders/:uuid/link-invoice-account'
        text={t('workorder.link_invoice_account')}
      />
      <Crumb
        data-testid='crumb-workorders-view-link-vehicle'
        key='crumb-workorders-view-link-vehicle'
        path='/workorders/:uuid/link-vehicle'
        text={t('workorder.link_vehicle')}
      />
      <Crumb
        data-testid='crumb-workorders-view-accounts-view'
        key='crumb-workorders-view-accounts-view'
        path='/workorders/:uuid/*accounts/:accountUUID'
        text={t('vehicle.edit_account')}
      />
      <Crumb
        data-testid='crumb-workorders-view-search-product'
        key='crumb-workorders-view-search-product'
        path='/workorders/:uuid/search-products/:orderAccountUUID/:searchTerm'
        text={params => `${t('global.search')} (${decodeURIComponent(params.searchTerm)})`}
      />
      <Crumb
        data-testid='crumb-workorders-view-vehicle-edit'
        key='crumb-workorders-view-vehicle-edit'
        path='/workorders/:uuid/*vehicles/:vehicleUUID'
        text={t('vehicle.edit_vehicle')}
      />
      <Crumb
        data-testid='crumb-workorders-haynespro-service-schedule'
        key='crumb-workorders-haynespro-service-schedule'
        path='/workorders/:uuid/*haynes/service-schedule/:conditionUUID?'
        text={t('vehicle.haynes_service_schedule')}
      />
      <Crumb
        data-testid='crumb-workorders-haynespro-service-schedule-intervaldata'
        key='crumb-workorders-haynespro-service-schedule-intervaldata'
        path='/workorders/:uuid/*haynes/service-schedule/:conditionUUID?/intervaldata'
        text={t('vehicle.haynes_service_interval_data')}
      />
      <Crumb
        data-testid='crumb-workorders-haynespro-repair-times'
        key='crumb-workorders-haynespro-repair-times'
        path='/workorders/:uuid/*haynes/repair-times'
        text={t('vehicle.haynes_repair_times')}
      />
      <Crumb
        data-testid='crumb-orders-haynespro-iframe'
        key='crumb-orders-haynespro-iframe'
        path='/workorders/:uuid/*haynes/frame'
        text={t('workorder.haynes_iframe')}
      />
      <Crumb
        data-testid='crumb-workorders-checklist-view'
        key='crumb-workorders-checklist-view'
        path='/workorders/:uuid/checklist/:checklistUUID'
        text={t('workorder.checklist')}
      />
      <Crumb
        data-testid='crumb-workorders-checklist-view-print'
        key='crumb-workorders-checklist-view-print'
        path='/workorders/:uuid/checklist/:checklistUUID/print'
        text={t('workorder.checklist_print')}
      />
      <Crumb
        data-testid='crumb-workorders-haynespro-view-repair-times'
        key='crumb-workorders-haynespro-view-repair-times'
        path='/workorders/:uuid/*haynes/repair-times/:nodeUUID'
        text={params => getRepairTimeChapterName(repairTimeChapters, params.nodeUUID)}
      />
      <Crumb
        data-testid='crumb-workorders-historic-parts'
        key='crumb-workorders-historic-parts'
        path='/workorders/:uuid/historic-parts'
        text={t('vehicle.order_history')}
      />
      <Crumb
        data-testid='crumb-workorders-courtesy-vehicle'
        key='crumb-workorders-courtesy-vehicle'
        path='/workorders/:uuid/courtesy-vehicle'
        exact
        text={t('workorder.CourtesyVehicle')}
      />
      <Crumb
        exact
        data-testid='crumb-workorders-courtesy-vehicle-edit'
        key='crumb-workorders-courtesy-vehicle-edit'
        path='/workorders/:uuid/courtesy-vehicle/:lineUUID'
        text={params => {
          const description = getLineDescription(lines, params.lineUUID)

          return description ? `${t('workorder.CourtesyVehicle')} (${description})` : t('workorder.CourtesyVehicle')
        }}
      />
      <Crumb
        data-testid='crumb-workorders-line-view-products'
        key='crumb-workorders-line-view-products'
        path='/workorders/:workorderUUID/*line/:lineUUID/products'
        text={params => getLineGenericArticleDescription(lines, params.lineUUID)}
      />
      <Crumb
        data-testid='crumb-workorders-view-product-groups-view-products-view'
        key='crumb-workorders-view-product-groups-view-products-view'
        path='/workorders/:uuid/*/products/:productID'
        text={() => getProductTitle(product)}
      />
      <Crumb
        data-testid='crumb-catalogs-universalparts'
        key='crumb-catalogs-universalparts'
        path='/workorders/*/catalogs/universal-parts'
        text={t('catalogs.UniversalParts')}
      />
      <Crumb
        data-testid='crumb-catalogs-toolspecial'
        key='crumb-catalogs-toolspecial'
        path='/workorders/*/catalogs/tool-special'
        text={t('catalogs.ToolSpecial')}
      />
      <Crumb
        data-testid='crumb-workorders-view-vehicle-road-assistance'
        key='crumb-workorders-view-vehicle-road-assistance'
        path='/workorders/:uuid/vehicle/:vehicleUUID'
        text={t('RoadAssistance.title')}
      />
      <Crumb
        data-testid='crumb-workorders-wom-connect'
        key='crumb-workorders-wom-connect'
        path={WomPlusUrl}
        text={t('setting.womplus.title')}
      />
      {!!productGroupCrumbs && React.Children.toArray(productGroupCrumbs)}
    </Breadcrumbs>
  )
}

export default WorkOrdersBreadCrumbs
